const fetchQbilonApi = async (rawBody) => {
    const myHeaders = new Headers();
    myHeaders.append("qbilon-tenant", process.env.REACT_APP_API_TENANT);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", "Basic " + process.env.REACT_APP_API_TOKEN); 

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: rawBody,
        redirect: "follow",
    };

    const res = await fetch(process.env.REACT_APP_API_URL + "/data", requestOptions);
    if (!res.ok) {
        throw new Error(`Fetch fehlgeschlagen: ${res.status} ${res.statusText}`);
    } else {
        return await res.json();
    }
}

const postQbilonApi = async (rawBody) => {
    const myHeaders = new Headers();
    myHeaders.append("qbilon-tenant", process.env.REACT_APP_API_TENANT);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", "Basic " + process.env.REACT_APP_API_TOKEN); 

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: rawBody,
        redirect: "follow",
    };

    const res = await fetch(process.env.REACT_APP_API_URL + "/data/upsert/-2", requestOptions);
    if (!res.ok) {
        const errorBody = await res.text();
        console.error('API Error Response:', errorBody);
        throw new Error(`Fetch fehlgeschlagen: ${res.status} ${res.statusText} - ${errorBody}`);
    } else {
        return await res.json();
    }
}

const deleteQbilonApi = async (rawBody) => {
    const myHeaders = new Headers();
    myHeaders.append("qbilon-tenant", process.env.REACT_APP_API_TENANT);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", "Basic " + process.env.REACT_APP_API_TOKEN); 

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: rawBody,
        redirect: "follow",
    };

    const res = await fetch(process.env.REACT_APP_API_URL + "/data/delete/-2", requestOptions);
    if (!res.ok) {
        throw new Error(`Fetch fehlgeschlagen: ${res.status} ${res.statusText}`);
    } else {
        return await res.json();
    }
}

export const QbilonService = {
    getAllProvidesEdges: async () => {
        const raw = JSON.stringify({
            relations: [
                {
                    names: ["provides [ServiceNow Unternehmen -> ServiceNow Service Offerings]"],
                    attributeNames: ["type", "First Seen", "Name"],
                },
            ],
        });

        const result = await fetchQbilonApi(raw)

        // Achtung: result.relations ist vermutlich ein Objekt mit Schlüssel = Relationsname
        const elements = result?.relations?.["provides [ServiceNow Unternehmen -> ServiceNow Service Offerings]"]?.elements ?? [];

        // Falls elements ein Array ist: mappen
        return elements.map((relation) => {
            // Parse date from Unix timestamp if it's a number
            let parsedDate = null;
            const firstSeen = relation.properties?.["First Seen"];
            if (firstSeen && typeof firstSeen === 'number') {
                parsedDate = new Date(firstSeen);
                // Validate the date is valid
                if (isNaN(parsedDate.getTime())) {
                    parsedDate = null;
                }
            } else if (firstSeen) {
                // Try to parse as date string
                const attemptedDate = new Date(firstSeen);
                if (!isNaN(attemptedDate.getTime())) {
                    parsedDate = attemptedDate;
                }
            }

            return {
                provider: relation.sourceQbilonId,
                tso: relation.targetQbilonId,
                type: relation.properties?.type || relation.properties?.Type || null,
                created: parsedDate,
                id: relation.qbilonId,
            };
        });
    },

    getAllProviderNodes: async () => {
        const raw = JSON.stringify({
            types: [
                {
                    names: ["ServiceNow Unternehmen"],
                    attributeNames: ["ServiceNow Name"],
                    condition: {
                        conditionType: "nested",
                        operator: "and",
                        condition: [
                            {
                                conditionType: "atomic",
                                attributeName: "ServiceNow UInactive",
                                operator: "equal_to",
                                value: false
                            },
                            {
                                conditionType: "atomic",
                                attributeName: "ServiceNow UGpGeneralBranch",
                                operator: "equal_to",
                                value: "IO"
                            }
                        ]
                    }
                },
            ],
        });

        const result = await fetchQbilonApi(raw);

        const elements = result?.types?.["ServiceNow Unternehmen"]?.elements ?? [];

        return elements.map((node) => ({
            qbilonId: node.qbilonId,
            name: node.properties["ServiceNow Name"] || "Unbenannt"
        }));
    },

    getAllServiceNodes: async () => {
        const raw = JSON.stringify({
            types: [
                {
                    names: ["ServiceNow Service Offerings"],
                    attributeNames: ["ServiceNow Name"],
                    condition: {
                        conditionType: "nested",
                        operator: "and",
                        condition: [
                            {
                                conditionType: "atomic",
                                attributeName: "ServiceNow ServiceClassification",
                                operator: "equal_to",
                                value: "Technical Service"
                            },
                            {
                                conditionType: "atomic",
                                attributeName: "ServiceNow InstallStatus",
                                operator: "equal_to",
                                value: 102
                            },
                            {
                                conditionType: "atomic",
                                attributeName: "ServiceNow Comments",
                                operator: "equal_to",
                                value: "#IO-TSO"
                            }
                        ]
                    }
                },
            ],
        });

        const result = await fetchQbilonApi(raw);

        const elements = result?.types?.["ServiceNow Service Offerings"]?.elements ?? [];

        return elements.map((node) => ({
            qbilonId: node.qbilonId,
            name: node.properties["ServiceNow Name"] || "Unbenannt"
        }));
    },

    createNewEdge: async (sourceQbilonId, targetQbilonId, type) => {        
        const raw = JSON.stringify({
            types: {},
            relations: {
                "provides [ServiceNow Unternehmen -> ServiceNow Service Offerings]": {
                    nameAttribute: "Name",
                    elements: [
                        {
                            targetQbilonId: targetQbilonId,
                            sourceQbilonId: sourceQbilonId,
                            externalId: sourceQbilonId +'_'+ targetQbilonId +'_'+ type,
                            properties: {
                                type: type,
                                Name: `Beziehung ${sourceQbilonId} -> ${targetQbilonId}`
                            }
                        }
                    ]
                }
            }
        })
        const result = await postQbilonApi(raw);
        return result;

    },

    deleteEdge: async (qbilonId) => {
        const raw = JSON.stringify({
            relationElements: [
                {
                    qbilonId: qbilonId
                },
            ],
        });

        const result = await deleteQbilonApi(raw);
        return result;
    }
}